import { t, Instance } from 'mobx-state-tree';
import {
  changeAppLanguage,
  readStoredPreferredLanguage,
  resolveAppLocale,
  writeStoredPreferredLanguage,
  type AppLocale
} from '@/i18n';

const SHOW_PETS_STORAGE_KEY = 'we-tree:ui:showPetsInRoot';

function getStoredShowPetsInRoot(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const stored = localStorage.getItem(SHOW_PETS_STORAGE_KEY);
    return stored === 'true';
  } catch {
    return false;
  }
}

function setStoredShowPetsInRoot(value: boolean): void {
  try {
    localStorage.setItem(SHOW_PETS_STORAGE_KEY, String(value));
  } catch {
    // ignore
  }
}

function getInitialPreferredLanguage(): AppLocale {
  return resolveAppLocale(readStoredPreferredLanguage());
}

/**
 * UIStore - UI state and preferences
 * Minimal implementation for backward compatibility during transition
 */
export const UIStore = t
  .model('UIStore', {
    // Pedigree: show pets in root generation (persisted to localStorage)
    showPetsInRoot: t.optional(t.boolean, false),

    // App UI language (persisted when the user explicitly chooses via the selector)
    preferredLanguage: t.optional(t.string, 'en'),

    // Family creation state
    familyCreationState: t.optional(t.string, 'idle'),
    familyCreationError: t.maybeNull(t.string),

    // Upload state
    uploadState: t.optional(t.string, 'idle'),
    uploadFile: t.maybeNull(t.frozen<File>()),
    uploadErrorMessage: t.maybeNull(t.string),
    // Active import tracking
    activeImportJobId: t.maybeNull(t.string),
    activeImportFamilyId: t.maybeNull(t.string)
  })
  .actions((self) => ({
    setShowPetsInRoot(value: boolean) {
      self.showPetsInRoot = value;
      setStoredShowPetsInRoot(value);
    },

    setPreferredLanguage(locale: AppLocale) {
      self.preferredLanguage = locale;
      writeStoredPreferredLanguage(locale);
      void changeAppLanguage(locale);
    },

    // Family creation actions
    setFamilyCreationState(state: string) {
      self.familyCreationState = state;
    },

    setFamilyCreationError(error: string | null) {
      self.familyCreationError = error;
    },

    // Upload actions
    setUploadState(state: string) {
      self.uploadState = state;
    },

    setUploadFile(file: File | null) {
      self.uploadFile = file;
    },

    setUploadErrorMessage(error: string | null) {
      self.uploadErrorMessage = error;
    },

    resetUploadState() {
      self.uploadState = 'idle';
      self.uploadFile = null;
      self.uploadErrorMessage = null;
    },

    // Active import actions
    setActiveImport(jobId: string | null, familyId: string | null) {
      self.activeImportJobId = jobId;
      self.activeImportFamilyId = familyId;
    },

    clearActiveImport() {
      self.activeImportJobId = null;
      self.activeImportFamilyId = null;
    },

    /**
     * Clear all UI state and reset to initial state
     * Called when user signs out to ensure fresh state for next user
     */
    clear() {
      self.familyCreationState = 'idle';
      self.familyCreationError = null;
      self.uploadState = 'idle';
      self.uploadFile = null;
      self.uploadErrorMessage = null;
      self.activeImportJobId = null;
      self.activeImportFamilyId = null;
    }
  }));

export type UIStoreType = Instance<typeof UIStore>;

// Export singleton instance (prefs loaded from localStorage / browser)
export const uiStore = UIStore.create({
  showPetsInRoot: getStoredShowPetsInRoot(),
  preferredLanguage: getInitialPreferredLanguage()
});
